import { gql } from "@apollo/client";

// ── Queries ──────────────────────────────────────────────────────────

export const GET_SESSIONS_LIST = gql`
  query GetSessionsList(
    $filter: BookingFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allBookings(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      state
      method
      availabilityId
      bookingTypeId
      applicablePricingPlanId
      primaryCustomerName
      deletedAt
      bookingType {
        id
        name
      }
      availability {
        id
        date
        startTime
        endTime
        canPhone
        canVideo
        canInPerson
        providerId
      }
    }
    _allBookingsMeta(filter: $filter) {
      count
    }
  }
`;

export const GET_SESSION_DETAIL = gql`
  query GetSessionDetail($id: String!, $includeDeleted: Boolean) {
    Booking(id: $id, includeDeleted: $includeDeleted) {
      id
      clientId
      state
      method
      availabilityId
      bookingTypeId
      applicablePricingPlanId
      primaryCustomerName
      bookingSituation
      agreementType
      legalName
      customerCount
      createdAt
      updatedAt
      deletedAt
      bookingType {
        id
        name
      }
      availability {
        id
        date
        startTime
        endTime
        canPhone
        canVideo
        canInPerson
        providerId
      }
      questions {
        isTimeSensitive
        isInvolvingTheCourt
        isPaperworkAvailable
      }
    }
  }
`;

export const GET_BOOKING_FILES = gql`
  query GetBookingFiles(
    $filter: BookingFileFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allBookingFiles(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      originalFilename
      fileSize
      mimeType
      createdAt
    }
  }
`;

export const GET_BOOKING_PAYMENTS = gql`
  query GetBookingPayments(
    $filter: PaymentFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allPayments(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      bookingId
      type
      state
      amount
      createdAt
    }
  }
`;

export const GET_BOOKING_MOVES = gql`
  query GetBookingMoves(
    $filter: BookingMoveFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allBookingMoves(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      bookingId
      fromAvailabilityId
      toAvailabilityId
      state
      reason
      createdAt
      toAvailability {
        id
        date
        startTime
        endTime
      }
    }
  }
`;

export const GET_BOOKING_CLOSURES = gql`
  query GetBookingClosures(
    $filter: BookingClosureFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allBookingClosures(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      bookingId
      totalMinutes
      totalAmount
      createdAt
    }
  }
`;

export const GET_BOOKING_SIGNATURES = gql`
  query GetBookingSignatures(
    $filter: BookingSignatureFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allBookingSignatures(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      bookingId
      legalName
      signedAt
      createdAt
    }
  }
`;

export const GET_PRIMARY_CUSTOMER = gql`
  query GetPrimaryCustomer($filter: PersonFilter) {
    allPeople(filter: $filter) {
      id
      primaryClientId
      name
      alias
      email
      phoneNumber
    }
  }
`;

export const GET_BOOKING_CUSTOMERS = gql`
  query GetBookingCustomers($filter: PersonFilter) {
    allPeople(filter: $filter) {
      id
      name
      alias
      email
      phoneNumber
    }
  }
`;

export const GET_PROVIDERS = gql`
  query GetProviders($perPage: Int, $sortField: String, $sortOrder: String) {
    allProviders(perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder) {
      id
      firstName
      lastName
    }
  }
`;

export const GET_AVAILABILITIES_RANGE = gql`
  query GetAvailabilitiesRange(
    $filter: AvailabilityFilter
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
  ) {
    allAvailabilities(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      id
      date
      startTime
      endTime
      canPhone
      canVideo
      canInPerson
      providerId
      bookedAt
    }
  }
`;

export const GET_PRICING_PLAN = gql`
  query GetPricingPlan($id: String!) {
    PricingPlan(id: $id) {
      id
      hourlyRate
    }
  }
`;

// ── Mutations ────────────────────────────────────────────────────────

export const CANCEL_BOOKING = gql`
  mutation CancelBooking($bookingId: ID!) {
    cancelBooking(bookingId: $bookingId) {
      id
      state
    }
  }
`;

export const MOVE_BOOKING = gql`
  mutation MoveBooking(
    $bookingId: ID!
    $newAvailabilityId: ID!
    $reason: String
  ) {
    moveBooking(
      bookingId: $bookingId
      newAvailabilityId: $newAvailabilityId
      reason: $reason
    ) {
      id
      state
    }
  }
`;

export const CLOSE_BOOKING = gql`
  mutation CloseBooking(
    $bookingId: ID!
    $preparationMinutes: Int!
    $meetingMinutes: Int!
    $followupMinutes: Int!
    $extraMinutes: Int!
    $discountPercentage: Float!
    $closureMessage: String!
  ) {
    closeBooking(
      bookingId: $bookingId
      preparationMinutes: $preparationMinutes
      meetingMinutes: $meetingMinutes
      followupMinutes: $followupMinutes
      extraMinutes: $extraMinutes
      discountPercentage: $discountPercentage
      closureMessage: $closureMessage
    ) {
      id
      state
    }
  }
`;

export const DELETE_BOOKING = gql`
  mutation DeleteBooking($bookingId: ID!) {
    deleteBooking(bookingId: $bookingId) {
      id
      deletedAt
    }
  }
`;

export const RESTORE_BOOKING = gql`
  mutation RestoreBooking($bookingId: ID!) {
    restoreBooking(bookingId: $bookingId) {
      id
      deletedAt
    }
  }
`;

export const GET_BOOKING_TYPES = gql`
  query GetBookingTypes($perPage: Int, $sortField: String, $sortOrder: String) {
    allBookingTypes(perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder) {
      id
      name
    }
  }
`;

export const CREATE_MANUAL_BOOKING = gql`
  mutation CreateManualBooking(
    $customerIds: [ID]!
    $providerId: ID!
    $bookingTypeId: ID!
    $method: String!
    $situation: String
    $availabilityId: ID
    $date: String
    $startTime: String
    $durationMinutes: Int
  ) {
    createManualBooking(
      customerIds: $customerIds
      providerId: $providerId
      bookingTypeId: $bookingTypeId
      method: $method
      situation: $situation
      availabilityId: $availabilityId
      date: $date
      startTime: $startTime
      durationMinutes: $durationMinutes
    ) {
      id
      state
    }
  }
`;

export const CREATE_SESSION = gql`
  mutation CreateSession($bookingId: ID!) {
    createSession(bookingId: $bookingId) {
      success
      created
      sessionId
      error
    }
  }
`;

export const GET_SESSION_BILLABLE = gql`
  query GetSessionBillable($bookingId: String!) {
    sessionStatus(bookingId: $bookingId) {
      elapsedSecondsBillable
      session {
        id
        startedAt
        endedAt
      }
      events {
        id
        eventType
        participantRole
        occurredAt
      }
      notes {
        id
        content
      }
      actionItems {
        id
        text
        isCompleted
        sortOrder
      }
    }
  }
`;

export const GENERATE_CLOSURE_SUMMARY = gql`
  mutation GenerateClosureSummary($bookingId: ID!) {
    generateClosureSummary(bookingId: $bookingId) {
      success
      summary
      cached
      error
    }
  }
`;

export const GET_SESSION_TRANSCRIPT = gql`
  query GetSessionTranscript($bookingId: String!) {
    sessionTranscript(bookingId: $bookingId) {
      sessionId
      ready
      generatedAt
      error
      stage
      totalTracks
      archivedTracks
      transcribedTracks
      mergedTracks
      failedTracks
      estimatedReadyAt
      turns {
        participantId
        participantRole
        participantName
        offsetSeconds
        startMs
        endMs
        text
      }
    }
  }
`;
